import React, { useState, useRef } from 'react';
import { UserAccount, GroupMemberProfile, NotificationSettings, PrivacySettings, FootballCardAttributes, FootballPosition, PreferredFoot, GoalkeeperAbility } from '../types';
import { NumberInput } from './NumberInput';
import { FootballCard } from './FootballCard';
import { ThemeToggle } from './ThemeToggle';
import { User, Shield, Bell, Lock, Palette, Key, LogOut, Trash2, Check, Award, Activity, Heart, Sparkles, Camera, Upload, Image as ImageIcon } from 'lucide-react';
import { compressImage } from '../utils/imageCompressor';

interface ProfileViewProps {
  user: UserAccount;
  member: GroupMemberProfile;
  notificationSettings: NotificationSettings;
  privacySettings: PrivacySettings;
  onUpdateUser: (updatedUser: UserAccount) => void;
  onUpdateMember: (updatedMember: GroupMemberProfile) => void;
  onUpdateNotifications: (updatedNotifications: NotificationSettings) => void;
  onUpdatePrivacy: (updatedPrivacy: PrivacySettings) => void;
  onDeleteAccount: () => void;
}

export const ProfileView: React.FC<ProfileViewProps> = ({
  user,
  member,
  notificationSettings,
  privacySettings,
  onUpdateUser,
  onUpdateMember,
  onUpdateNotifications,
  onUpdatePrivacy,
  onDeleteAccount
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'edit_profile' | 'football_pref' | 'notifications' | 'privacy' | 'appearance' | 'account'>('overview');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Form states for Edit Profile
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fullName, setFullName] = useState(user.fullName);
  const [displayName, setDisplayName] = useState(user.displayName);
  const [nickname, setNickname] = useState(user.nickname);
  const [bio, setBio] = useState(user.bio);
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.phoneNumber || '');
  const [dob, setDob] = useState(user.dateOfBirth || '');
  const [avatarUrl, setAvatarUrl] = useState(user.avatar);
  const [jerseyNumber, setJerseyNumber] = useState(member.jerseyNumber);
  const [preferredFoot, setPreferredFoot] = useState<PreferredFoot>(member.preferredFoot);
  const [primaryPosition, setPrimaryPosition] = useState<FootballPosition>(member.primaryPosition);

  // Sample Football Avatar Presets
  const AVATAR_PRESETS = [
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=400&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400&auto=format&fit=crop&q=80',
  ];

  const handlePhotoFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size exceeds 10MB limit. Please choose a smaller image.');
        return;
      }
      try {
        const compressed = await compressImage(file, 250, 250, 0.75);
        setAvatarUrl(compressed);
        onUpdateUser({ ...user, avatar: compressed });
        onUpdateMember({ ...member, avatar: compressed });
        showSaveNotice();
      } catch (err) {
        console.error('Failed to compress image:', err);
      }
    }
  };

  // Customization
  const [favClub, setFavClub] = useState(user.favoriteClub);
  const [favPlayer, setFavPlayer] = useState(user.favoritePlayer);
  const [favNationalTeam, setFavNationalTeam] = useState(user.favoriteNationalTeam);

  // Football Preferences
  const [gkAbility, setGkAbility] = useState<GoalkeeperAbility>(member.goalkeeperAbility);
  const [prefColor, setPrefColor] = useState(member.preferredTeamColor);
  const [compPref, setCompPref] = useState(member.competitivePreference);

  // Notifications State
  const [notifs, setNotifs] = useState<NotificationSettings>({ ...notificationSettings });

  // Privacy State
  const [privacy, setPrivacy] = useState<PrivacySettings>({ ...privacySettings });

  // Account Pass State
  const [passMessage, setPassMessage] = useState('');

  const showSaveNotice = () => {
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdateUser({
      ...user,
      fullName,
      displayName,
      nickname,
      bio,
      email,
      phoneNumber: phone,
      dateOfBirth: dob,
      avatar: avatarUrl,
      favoriteClub: favClub,
      favoritePlayer: favPlayer,
      favoriteNationalTeam: favNationalTeam,
    });

    onUpdateMember({
      ...member,
      displayName,
      jerseyNumber,
      preferredFoot,
      primaryPosition,
      goalkeeperAbility: gkAbility,
      preferredTeamColor: prefColor,
      competitivePreference: compPref,
    });

    showSaveNotice();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-24">
      
      {/* Profile Banner & Header Card */}
      <div className="relative rounded-3xl overflow-hidden bg-slate-900 border border-slate-800 shadow-2xl">
        <div className="h-32 sm:h-44 w-full relative">
          <img src={user.coverBannerUrl} alt="Cover Banner" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
        </div>

        <div className="px-6 pb-6 pt-0 relative flex flex-wrap sm:flex-nowrap items-end justify-between gap-4 -mt-12">
          <div className="flex items-end gap-4">
            <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden border-4 border-slate-900 shadow-xl bg-slate-950 group">
              <img src={user.avatar} alt={user.displayName} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center text-white text-[10px] font-bold gap-1 cursor-pointer"
                title="Click to upload profile photo"
              >
                <Camera className="w-5 h-5 text-emerald-400" />
                <span>Upload Photo</span>
              </button>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handlePhotoFileSelect}
              className="hidden"
            />
            <div className="mb-1">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-black text-white">{user.displayName}</h2>
                <span className="text-xs font-mono font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded-full">
                  #{member.jerseyNumber}
                </span>
              </div>
              <p className="text-xs text-slate-400 flex items-center gap-2">
                <span>"{user.nickname}"</span> • <span className="text-emerald-400 font-bold">{member.primaryPosition}</span> • <span>{member.preferredFoot} Foot</span>
              </p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <span className="text-xs font-extrabold uppercase px-3 py-1 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
              Group OVR {member.footballCard?.overall ?? 0}
            </span>
            <span className="text-[10px] font-bold text-slate-400 bg-slate-800/80 px-2.5 py-1 rounded-lg border border-slate-700/60 flex items-center gap-1">
              <Shield className="w-3 h-3 text-lime-400" /> Group Profile: <span className="text-white font-extrabold">GigaBalls FC</span>
            </span>
          </div>
        </div>

        {/* Group Profile Scope Explainer */}
        <div className="bg-slate-950/90 border-t border-slate-800 px-6 py-2.5 flex items-center justify-between text-[11px] text-slate-400">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-lime-400 shrink-0" />
            <span>
              Football settings (<strong>Pos: {member.primaryPosition}</strong>, <strong>Jersey #{member.jerseyNumber}</strong>, <strong>Foot: {member.preferredFoot}</strong>, <strong>OVR: {member.footballCard?.overall ?? 0}</strong>) are tied strictly to your <strong>Group Member Profile</strong>.
            </span>
          </div>
          <span className="hidden sm:inline font-mono text-[10px] text-lime-400 bg-lime-400/10 px-2 py-0.5 rounded border border-lime-400/20">
            Separate from User Account
          </span>
        </div>
      </div>

      {/* Sub-Tabs Navigation Bar */}
      <div className="flex border-b border-slate-800 bg-slate-950/80 p-1 rounded-2xl overflow-x-auto no-scrollbar text-xs font-bold text-slate-400">
        {[
          { id: 'overview', label: 'Overview & Card' },
          { id: 'edit_profile', label: 'Edit Profile' },
          { id: 'football_pref', label: 'Preferences' },
          { id: 'notifications', label: 'Notifications' },
          { id: 'privacy', label: 'Privacy' },
          { id: 'appearance', label: 'Appearance' },
          { id: 'account', label: 'Account Settings' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`py-2.5 px-4 rounded-xl whitespace-nowrap transition ${
              activeTab === tab.id
                ? 'bg-emerald-500 text-slate-950 font-black shadow'
                : 'hover:text-slate-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {saveSuccess && (
        <div className="bg-emerald-500 text-slate-950 font-extrabold text-xs p-3 rounded-2xl flex items-center justify-center gap-2 shadow-lg">
          <Check className="w-4 h-4" /> Profile & Settings Updated Successfully!
        </div>
      )}

      {/* TAB 1: OVERVIEW & CARD */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Football Card Component */}
          <div className="space-y-4">
            <h3 className="text-xs font-extrabold uppercase text-slate-400 tracking-wider">
              Official Player Card
            </h3>
            <FootballCard
              name={user.displayName}
              nickname={user.nickname}
              position={member.primaryPosition}
              avatar={user.avatar}
              card={member.footballCard}
              jerseyNumber={member.jerseyNumber}
              preferredFoot={member.preferredFoot}
              cosmeticTheme={member.cardCosmetic || 'default'}
              onShare={() => alert(`Sharing card for ${user.displayName}!`)}
            />
          </div>

          {/* Player Statistics Grid & Achievements */}
          <div className="space-y-4">
            <h3 className="text-xs font-extrabold uppercase text-slate-400 tracking-wider">
              Career Match Statistics
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 font-mono text-center">
              <div className="bg-[#121614] border border-white/5 p-3 rounded-xl">
                <span className="text-[10px] text-slate-400 block font-sans">Matches Played</span>
                <span className="text-xl font-bold text-white">{member.stats.matchesPlayed}</span>
              </div>
              <div className="bg-[#121614] border border-white/5 p-3 rounded-xl">
                <span className="text-[10px] text-slate-400 block font-sans">Goals Scored</span>
                <span className="text-xl font-bold text-[#a8c936]">{member.stats.goals}</span>
              </div>
              <div className="bg-[#121614] border border-white/5 p-3 rounded-xl">
                <span className="text-[10px] text-slate-400 block font-sans">Assists</span>
                <span className="text-xl font-bold text-white">{member.stats.assists}</span>
              </div>
              <div className="bg-[#121614] border border-white/5 p-3 rounded-xl">
                <span className="text-[10px] text-slate-400 block font-sans">MOTM Awards</span>
                <span className="text-xl font-bold text-amber-400">⭐ {member.stats.motmCount}</span>
              </div>
              <div className="bg-[#121614] border border-white/5 p-3 rounded-xl">
                <span className="text-[10px] text-slate-400 block font-sans">Avg Rating</span>
                <span className="text-xl font-bold text-[#a8c936]">{member.stats.averageRating.toFixed(1)}</span>
              </div>
              <div className="bg-[#121614] border border-white/5 p-3 rounded-xl">
                <span className="text-[10px] text-slate-400 block font-sans">Win %</span>
                <span className="text-xl font-bold text-white">{member.stats.winPct}%</span>
              </div>
            </div>

            {/* Customization Fun Info */}
            <div className="bg-[#121614] border border-white/5 p-4 rounded-xl space-y-2 text-xs">
              <span className="font-bold text-white block">Football Identity</span>
              <div className="flex justify-between text-slate-400">
                <span>Favorite Club:</span>
                <span className="font-semibold text-white">{user.favoriteClub}</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Favorite Player:</span>
                <span className="font-semibold text-white">{user.favoritePlayer}</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>National Team:</span>
                <span className="font-semibold text-white">{user.favoriteNationalTeam}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: EDIT PROFILE */}
      {activeTab === 'edit_profile' && (
        <form onSubmit={handleSaveProfile} className="bg-slate-900 border border-slate-800 p-5 rounded-3xl space-y-4 text-xs">
          <h3 className="font-extrabold text-sm uppercase text-emerald-400 tracking-wider">
            Edit Personal Information & Profile Photo
          </h3>

          {/* Profile Photo Uploader Card */}
          <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-extrabold text-white flex items-center gap-2">
                <Camera className="w-4 h-4 text-emerald-400" /> Profile Picture
              </span>
              <span className="text-[10px] text-slate-400">JPG, PNG, WEBP (Max 5MB)</span>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <img src={avatarUrl} alt="Avatar Preview" className="w-16 h-16 rounded-2xl object-cover border-2 border-emerald-500/50 shadow-lg" />
              <div className="flex-1 space-y-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 font-bold border border-emerald-500/40 rounded-xl transition flex items-center gap-2 text-xs"
                >
                  <Upload className="w-4 h-4" /> Upload New Photo from Device
                </button>
                <p className="text-[10px] text-slate-400">Or pick a preset avatar below:</p>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1 overflow-x-auto no-scrollbar">
              {AVATAR_PRESETS.map((preset, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    setAvatarUrl(preset);
                    onUpdateUser({ ...user, avatar: preset });
                    onUpdateMember({ ...member, avatar: preset });
                    showSaveNotice();
                  }}
                  className={`w-10 h-10 rounded-xl overflow-hidden border-2 transition shrink-0 ${
                    avatarUrl === preset ? 'border-emerald-400 ring-2 ring-emerald-400/40 scale-105' : 'border-slate-800 opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={preset} alt={`Preset ${idx}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-300 font-bold mb-1">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 text-white p-3 rounded-xl focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-slate-300 font-bold mb-1">Display Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 text-white p-3 rounded-xl focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-slate-300 font-bold mb-1">Nickname</label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 text-amber-300 font-bold p-3 rounded-xl focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-slate-300 font-bold mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 text-white p-3 rounded-xl focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-slate-300 font-bold mb-1">Phone Number (Optional)</label>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 text-white p-3 rounded-xl focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-slate-300 font-bold mb-1">Date of Birth (Optional)</label>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 text-white p-3 rounded-xl focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-300 font-bold mb-1">Bio</label>
            <textarea
              rows={2}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 text-white p-3 rounded-xl focus:outline-none"
            />
          </div>

          <div className="pt-2 border-t border-slate-800">
            <h4 className="font-extrabold text-amber-400 mb-2">Profile Customization Badges</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-slate-400 font-bold mb-1">Favorite Club</label>
                <input
                  type="text"
                  value={favClub}
                  onChange={(e) => setFavClub(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 text-amber-300 font-bold p-2.5 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-slate-400 font-bold mb-1">Favorite Player</label>
                <input
                  type="text"
                  value={favPlayer}
                  onChange={(e) => setFavPlayer(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 text-amber-300 font-bold p-2.5 rounded-xl"
                />
              </div>
              <div>
                <label className="block text-slate-400 font-bold mb-1">National Team</label>
                <input
                  type="text"
                  value={favNationalTeam}
                  onChange={(e) => setFavNationalTeam(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 text-amber-300 font-bold p-2.5 rounded-xl"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs rounded-xl transition shadow-lg shadow-emerald-500/20"
          >
            Save Profile Changes
          </button>
        </form>
      )}

      {/* TAB 3: FOOTBALL PREFERENCES */}
      {activeTab === 'football_pref' && (
        <form onSubmit={handleSaveProfile} className="bg-slate-900 border border-slate-800 p-5 rounded-3xl space-y-4 text-xs">
          <h3 className="font-extrabold text-sm uppercase text-blue-400 tracking-wider">
            Football & Team Balancing Preferences
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-300 font-bold mb-1">Preferred Foot</label>
              <select
                value={preferredFoot}
                onChange={(e) => setPreferredFoot(e.target.value as PreferredFoot)}
                className="w-full bg-slate-950 border border-slate-700 text-white font-bold p-3 rounded-xl"
              >
                <option value="Right">Right</option>
                <option value="Left">Left</option>
                <option value="Both">Both</option>
              </select>
            </div>

            <div>
              <label className="block text-slate-300 font-bold mb-1">Can Play Goalkeeper?</label>
              <select
                value={gkAbility}
                onChange={(e) => setGkAbility(e.target.value as GoalkeeperAbility)}
                className="w-full bg-slate-950 border border-slate-700 text-white font-bold p-3 rounded-xl"
              >
                <option value="Yes">Yes (Willing to play GK)</option>
                <option value="Sometimes">Sometimes</option>
                <option value="No">No (Outfield only)</option>
              </select>
            </div>

            <div>
              <label className="block text-slate-300 font-bold mb-1">Competitive / Casual Preference</label>
              <select
                value={compPref}
                onChange={(e) => setCompPref(e.target.value as any)}
                className="w-full bg-slate-950 border border-slate-700 text-white font-bold p-3 rounded-xl"
              >
                <option value="Competitive">Competitive (High intensity)</option>
                <option value="Casual">Casual (Fun & chill)</option>
                <option value="Both">Both</option>
              </select>
            </div>

            <div>
              <label className="block text-slate-300 font-bold mb-1">Preferred Jersey Number</label>
              <NumberInput
                value={jerseyNumber}
                onCommit={setJerseyNumber}
                min={0}
                className="w-full bg-slate-950 border border-slate-700 text-amber-300 font-mono font-bold p-3 rounded-xl"
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs rounded-xl transition shadow-lg"
          >
            Save Football Preferences
          </button>
        </form>
      )}

      {/* TAB 4: NOTIFICATIONS */}
      {activeTab === 'notifications' && (
        <div className="bg-slate-900 border border-slate-800 p-5 rounded-3xl space-y-4 text-xs">
          <h3 className="font-extrabold text-sm uppercase text-purple-400 tracking-wider">
            Individual Notification Controls
          </h3>

          <div className="space-y-2">
            {[
              { key: 'newMatchCreated', label: 'New Match Created' },
              { key: 'matchInvitation', label: 'Match Invitation' },
              { key: 'rsvpReminder', label: 'RSVP Reminder' },
              { key: 'matchStartingSoon', label: 'Match Starting Soon' },
              { key: 'teamAnnounced', label: 'Team & Lineup Announced' },
              { key: 'venueChanged', label: 'Venue or Time Changed' },
              { key: 'paymentReminder', label: 'Payment Pending Reminder' },
              { key: 'resultPosted', label: 'Match Score & Result Posted' },
              { key: 'motmVoting', label: 'MOTM Voting Open' },
            ].map((item) => (
              <div key={item.key} className="flex justify-between items-center bg-slate-950 p-3 rounded-xl border border-slate-800">
                <span className="font-bold text-slate-200">{item.label}</span>
                <input
                  type="checkbox"
                  checked={(notifs as any)[item.key]}
                  onChange={(e) => {
                    const next = { ...notifs, [item.key]: e.target.checked };
                    setNotifs(next);
                    onUpdateNotifications(next);
                  }}
                  className="w-4 h-4 accent-emerald-500"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 5: PRIVACY */}
      {activeTab === 'privacy' && (
        <div className="bg-slate-900 border border-slate-800 p-5 rounded-3xl space-y-4 text-xs">
          <h3 className="font-extrabold text-sm uppercase text-amber-400 tracking-wider">
            Privacy & Visibility Settings
          </h3>

          <div className="space-y-3">
            {[
              { key: 'phoneNumber', label: 'Phone Number' },
              { key: 'email', label: 'Email Address' },
              { key: 'paymentHistory', label: 'Payment History & Ledger' },
              { key: 'attendance', label: 'Attendance Records' },
            ].map((item) => (
              <div key={item.key} className="flex justify-between items-center bg-slate-950 p-3 rounded-2xl border border-slate-800">
                <span className="font-bold text-slate-200">{item.label}</span>
                <select
                  value={(privacy as any)[item.key]}
                  onChange={(e) => {
                    const next = { ...privacy, [item.key]: e.target.value };
                    setPrivacy(next);
                    onUpdatePrivacy(next);
                  }}
                  className="bg-slate-900 border border-slate-700 text-emerald-400 font-bold rounded-lg p-1.5 focus:outline-none"
                >
                  <option value="everyone">Everyone in Group</option>
                  <option value="organizers_only">Organizers Only</option>
                  <option value="private">Private</option>
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 6: APPEARANCE */}
      {activeTab === 'appearance' && (
        <div className="bg-slate-900 border border-slate-800 p-5 rounded-3xl space-y-4">
          <ThemeToggle />
        </div>
      )}

      {/* TAB 7: ACCOUNT */}
      {activeTab === 'account' && (
        <div className="bg-slate-900 border border-slate-800 p-5 rounded-3xl space-y-4 text-xs">
          <h3 className="font-extrabold text-sm uppercase text-red-400 tracking-wider">
            Account Management
          </h3>

          <div className="p-4 bg-red-950/20 border border-red-500/30 rounded-2xl space-y-2">
            <span className="font-black text-red-400 text-sm block">Delete Account</span>
            <p className="text-slate-300 leading-relaxed">
              If you delete your account, historical match stats will remain safely associated with an anonymized player record so group match results are preserved.
            </p>
            <button
              onClick={() => {
                if (confirm('Are you sure you want to delete your account? Historical match records will be anonymized.')) {
                  onDeleteAccount();
                }
              }}
              className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white font-bold rounded-xl transition"
            >
              Anonymize & Delete Account
            </button>
          </div>
        </div>
      )}

    </div>
  );
};
